import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import "@/index.css";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import WaterBackground from "@/components/WaterBackground";
import SiteBackground from "@/components/SiteBackground";
import EmergencyBanner from "@/components/EmergencyBanner";

import Home from "@/pages/Home";
import Packages from "@/pages/Packages";
import PackageDetail from "@/pages/PackageDetail";
import CustomPage from "@/pages/CustomPage";
import Contact from "@/pages/Contact";
import AdminLogin from "@/pages/AdminLogin";
import AuthCallback from "@/pages/AuthCallback";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import PagesManager from "@/pages/admin/PagesManager";
import PackagesManager from "@/pages/admin/PackagesManager";
import LeadsManager from "@/pages/admin/LeadsManager";
import AdminsManager from "@/pages/admin/AdminsManager";
import TestimonialsManager from "@/pages/admin/TestimonialsManager";
import GalleryManager from "@/pages/admin/GalleryManager";
import SitePagesManager from "@/pages/admin/SitePagesManager";
import NavigationManager from "@/pages/admin/NavigationManager";
import ContactFormSettings from "@/pages/admin/ContactFormSettings";
import PortalManager from "@/pages/admin/PortalManager";
import ImageEditor from "@/pages/admin/ImageEditor";
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalLayout from "@/pages/portal/PortalLayout";
import PortalVideos from "@/pages/portal/PortalVideos";
import PortalManuals from "@/pages/portal/PortalManuals";
import PortalReorder from "@/pages/portal/PortalReorder";
import PortalQA from "@/pages/portal/PortalQA";
import PortalContact from "@/pages/portal/PortalContact";

const ProtectedAdmin = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Authenticating…</div>;
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!user.is_admin) return <Navigate to="/admin/login?denied=1" replace />;
  return children;
};

const PublicShell = ({ children }) => (
  <>
    <WaterBackground />
    <SiteBackground />
    <Navbar />
    {children}
    <EmergencyBanner />
  </>
);

const AppRouter = () => {
  const location = useLocation();
  // CRITICAL: detect session_id during render to prevent race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<PublicShell><Home /></PublicShell>} />
      <Route path="/equipment" element={<PublicShell><Packages /></PublicShell>} />
      <Route path="/equipment/:slug" element={<PublicShell><PackageDetail /></PublicShell>} />
      <Route path="/contact" element={<PublicShell><Contact /></PublicShell>} />
      {/* Back-compat: old /packages URLs still work */}
      <Route path="/packages" element={<PublicShell><Packages /></PublicShell>} />
      <Route path="/packages/:slug" element={<PublicShell><PackageDetail /></PublicShell>} />
      <Route path="/p/:slug" element={<PublicShell><CustomPage /></PublicShell>} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/portal" element={<PortalLayout />}>
        <Route index element={<PortalVideos />} />
        <Route path="manuals" element={<PortalManuals />} />
        <Route path="reorder" element={<PortalReorder />} />
        <Route path="qa" element={<PortalQA />} />
        <Route path="contact" element={<PortalContact />} />
      </Route>
      <Route
        path="/admin"
        element={
          <ProtectedAdmin>
            <AdminLayout />
          </ProtectedAdmin>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="pages" element={<PagesManager />} />
        <Route path="navigation" element={<NavigationManager />} />
        <Route path="site-pages" element={<SitePagesManager />} />
        <Route path="packages" element={<PackagesManager />} />
        <Route path="testimonials" element={<TestimonialsManager />} />
        <Route path="gallery" element={<GalleryManager />} />
        <Route path="leads" element={<LeadsManager />} />
        <Route path="contact-form" element={<ContactFormSettings />} />
        <Route path="portal" element={<PortalManager />} />
        <Route path="image-editor" element={<ImageEditor />} />
        <Route path="admins" element={<AdminsManager />} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster
              position="top-right"
              theme="dark"
              toastOptions={{
                style: { background: "#0f172a", color: "#f8fafc", border: "1px solid rgba(255,255,255,0.08)" },
              }}
            />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
